// Rooftops, the launch map from GDD 10.2. A Hong Kong roof built as an arena for eight.
// Same conventions as map.ts: x east, y up, z north, positions are box centres, sizes are half
// extents. The floor top is y = 0. The map is 56 m across. Every number here is in metres.
//
// Layout, north up:
//   - a raised central platform (the plant room roof) 14 by 14 with its top at 3.6, standing on
//     four pillars with an open floor under it. The Sticky pad is under it, the Warhead on top
//   - a tower at the north east with its top at 6.8, reached by a cage stair from the north
//     walkway on its west side and by a ramp from the north east roof on its east side
//   - a ring of walkways at 2.0 and corner roofs at 2.2 around the outside, joined to the floor
//     by ramps and to each other by 0.2 steps, with parapets along the inner walkway edges
//   - the platform is reached by a switchback ramp on the east, a plank bridge from a hut on
//     the west (the hut itself is climbed from an AC unit), and a crate stack on the south
//   - AC units, crates and containers make the pockets, a water tank on stilts sits in each corner
//     and the neighbouring buildings wall the whole roof in
import { Kind } from "./tuning.js";
const paper = "#F2F2EE";
const slate = "#9AA0B4";
const orange = "#FF7A1A";
const red = "#FF3B3B";
const brown = "#B08A5A";
const white = "#FFFFFF";
export const ROOFTOPS_HALF = 28;
const RAMP_HY = 0.05; // ramps are 10 cm planks. Thin enough that their ends never cut into a neighbour
/** Axis aligned box from its centre, half extents and colour. */
function box(x, y, z, hx, hy, hz, color) {
    return { x, y, z, hx, hy, hz, color };
}
/** Axis aligned box from its footprint and top height, standing on the surface at yBase. */
function block(x, z, hx, hz, yBase, top, color) {
    return { x, y: (yBase + top) / 2, z, hx, hy: (top - yBase) / 2, hz, color };
}
/**
 * A ramp running along z from (zLow, yLow) up to (zHigh, yHigh), rotated about x.
 * The walking surface starts 2 * RAMP_HY above yLow (an auto step) and ends flush at yHigh, so the
 * high end meets the face of whatever it leads to and the low corner rests on the surface below.
 */
function rampZ(x, hx, zLow, zHigh, yLow, yHigh, color = orange) {
    const run = zHigh - zLow;
    const lift = 2 * RAMP_HY;
    const rise = yHigh - (yLow + lift);
    const len = Math.hypot(run, rise);
    const nY = Math.abs(run) / len;
    const nZ = -Math.sign(run) * rise / len;
    const sy = yLow + lift + rise / 2;
    const sz = (zLow + zHigh) / 2;
    return { x, y: sy - RAMP_HY * nY, z: sz - RAMP_HY * nZ, hx, hy: RAMP_HY, hz: len / 2, color, rx: -Math.sign(run) * Math.atan2(rise, Math.abs(run)) };
}
/** The same ramp running along x, rotated about z. */
function rampX(z, hz, xLow, xHigh, yLow, yHigh, color = orange) {
    const run = xHigh - xLow;
    const lift = 2 * RAMP_HY;
    const rise = yHigh - (yLow + lift);
    const len = Math.hypot(run, rise);
    const nX = -Math.sign(run) * rise / len;
    const nY = Math.abs(run) / len;
    const sx = (xLow + xHigh) / 2;
    const sy = yLow + lift + rise / 2;
    return { x: sx - RAMP_HY * nX, y: sy - RAMP_HY * nY, z, hx: len / 2, hy: RAMP_HY, hz, color, rz: Math.sign(run) * Math.atan2(rise, Math.abs(run)) };
}
/**
 * A stair of stacked boxes running along x from xStart, each tread deep and riser high, standing on
 * yBase. Step k has its top at yBase + riser * k. The last tread ends at xStart + sign * steps * tread.
 */
function stairX(xStart, dir, steps, tread, riser, z, hz, yBase, color) {
    const out = [];
    for (let k = 1; k <= steps; k++) {
        const x0 = xStart + dir * (k - 1) * tread;
        const x1 = xStart + dir * k * tread;
        out.push(block((x0 + x1) / 2, z, tread / 2, hz, yBase, yBase + riser * k, color));
    }
    return out;
}
// ---- heights
const WALK = 2.0; // walkway tops
const ROOF = 2.2; // corner roof tops, a 0.2 step up from the walkways
const PLAT = 3.6; // central platform top
const PLAT_BOTTOM = 3.0; // underside of the platform slab, the pillars reach this
const TOWER = 6.8; // tower top, 16 risers of 0.3 above the north walkway
const LANDING = 1.8; // the east ramp landing, the platform mid level
const HUT = 2.2; // the west hut top
const H = ROOFTOPS_HALF;
const boxes = [
    // ---- floor and the neighbouring buildings
    box(0, -0.5, 0, H + 2, 0.5, H + 2, paper),
    block(0, H + 1, H + 2, 1, 0, 8, slate),
    block(0, -H - 1, H + 2, 1, 0, 8, slate),
    block(H + 1, 0, 1, H, 0, 8, slate),
    block(-H - 1, 0, 1, H, 0, 8, slate),
    // ---- central platform: slab on four pillars, open floor under it
    block(0, 0, 7, 7, PLAT_BOTTOM, PLAT, paper),
    block(5.5, 5.5, 0.6, 0.6, 0, PLAT_BOTTOM, slate),
    block(-5.5, 5.5, 0.6, 0.6, 0, PLAT_BOTTOM, slate),
    block(5.5, -5.5, 0.6, 0.6, 0, PLAT_BOTTOM, slate),
    block(-5.5, -5.5, 0.6, 0.6, 0, PLAT_BOTTOM, slate),
    // ---- east route up the platform: a switchback. Floor to the landing, then north along the
    // platform's east face up to a column whose top is level with the platform
    rampX(-7.5, 1.5, 15, 10, 0, LANDING),
    block(8.5, -7.5, 1.5, 1.5, 0, LANDING, paper),
    rampZ(8.5, 1.5, -6, -1, LANDING, PLAT),
    block(8.5, -0.5, 1.5, 0.5, 0, PLAT, paper),
    // ---- west route: a hut with an AC unit as the step up, then a plank bridge to the platform
    block(-13.5, 0, 2.5, 3, 0, HUT, paper),
    block(-14.75, -4.25, 1.25, 1.25, 0, 1.1, slate),
    rampX(0, 0.75, -11, -7, HUT, PLAT),
    // ---- south route: an AC unit, a container, then the platform
    block(-8.25, -8.2, 1.25, 1.2, 0, 1.2, slate),
    block(-4, -8.2, 3, 1.2, 0, 2.5, brown),
    // ---- the ring: walkways at 2.0
    block(0, -H + 2, 18, 2, 0, WALK, paper), // south
    block(-5, H - 2, 13, 2, 0, WALK, paper), // north, from the north west roof to the tower
    block(H - 2, 0, 2, 18, 0, WALK, paper), // east
    block(-H + 2, 0, 2, 18, 0, WALK, paper), // west
    // ---- corner roofs at 2.2
    block(-23, -23, 5, 5, 0, ROOF, paper), // south west
    block(23, -23, 5, 5, 0, ROOF, paper), // south east
    block(-23, 23, 5, 5, 0, ROOF, paper), // north west
    block(23, 23, 5, 5, 0, ROOF, paper), // north east
    // ---- ramps from the floor to the ring
    rampZ(-12, 1.5, -19, -24, 0, WALK), // south walkway, west ramp
    rampZ(12, 1.5, -19, -24, 0, WALK), // south walkway, east ramp
    rampX(-6, 1.5, 19, 24, 0, WALK), // east walkway
    rampX(6, 1.5, -19, -24, 0, WALK), // west walkway
    rampZ(-15.5, 1.5, 19, 24, 0, WALK), // north walkway
    // ---- parapets along the inner walkway edges, with gaps where the ramps land
    block(-15.75, -24.15, 2.25, 0.15, WALK, WALK + 0.9, slate),
    block(0, -24.15, 10.5, 0.15, WALK, WALK + 0.9, slate),
    block(15.75, -24.15, 2.25, 0.15, WALK, WALK + 0.9, slate),
    block(-7.4, 24.15, 6.6, 0.15, WALK, WALK + 0.9, slate),
    block(24.15, -12.75, 0.15, 5.25, WALK, WALK + 0.9, slate),
    block(24.15, 6.75, 0.15, 11.25, WALK, WALK + 0.9, slate),
    block(-24.15, -6.75, 0.15, 11.25, WALK, WALK + 0.9, slate),
    block(-24.15, 12.75, 0.15, 5.25, WALK, WALK + 0.9, slate),
    // ---- the tower, with the cage stair on its west side and the ramp on its east
    block(13, 23, 5, 5, 0, TOWER, slate),
    ...stairX(-0.8, 1, 16, 0.55, 0.3, 26, 2, WALK, red),
    rampX(26, 2, 26, 18, ROOF, TOWER),
    // ---- steps up to the north east roof and down from the south east roof
    block(21.25, 16.75, 1.25, 1.25, 0, 1.1, slate),
    block(16.75, -21.25, 1.25, 1.25, 0, 1.1, slate),
    // ---- water tanks on stilts in each corner
    ...tank(-23, -23, ROOF),
    ...tank(23, -23, ROOF),
    ...tank(-23, 23, ROOF),
    ...tank(23, 20.5, ROOF),
    // ---- AC clusters in the south west, with the Bouncer lane between them
    block(-19, -12, 1, 1, 0, 1.4, slate),
    block(-19, -16, 1.25, 1, 0, 1.2, slate),
    block(-16.5, -20.5, 1, 1, 0, 1.6, slate),
    block(-10, -11, 1, 1.2, 0, 1.3, slate),
    block(-10, -15, 1, 1, 0, 1.6, slate),
    block(-8.5, -19.5, 1, 1, 0, 1.1, slate),
    // ---- containers and crates
    block(-18, 12.2, 3, 1.2, 0, 2.5, brown),
    block(13, -12.8, 3, 1.2, 0, 2.5, brown),
    block(-9, 9.5, 0.6, 0.6, 0, 1.2, red),
    block(-7.5, 10.7, 0.5, 0.5, 0, 1.0, red),
    block(20, -11, 0.7, 0.7, 0, 1.4, red),
    block(21.6, -12.4, 0.5, 0.5, 0, 1.0, red),
    block(2, 20, 0.6, 0.6, 0, 1.2, red),
    // ---- low walls on the floor
    block(20.5, 2, 0.5, 4, 0, 1.0, slate),
    block(-5, 16.4, 3, 0.4, 0, 1.0, slate),
];
/** A water tank on four stilts. The tank body starts 2.5 m above the roof so a player fits under it. */
function tank(x, z, yBase) {
    const out = [];
    for (const sx of [-1, 1])
        for (const sz of [-1, 1]) {
            out.push(block(x + sx, z + sz, 0.25, 0.25, yBase, yBase + 2.5, slate));
        }
    out.push(block(x, z, 1.5, 1.5, yBase + 2.5, yBase + 4.8, white));
    return out;
}
/** Yaw that faces the map centre from (x, z). Forward is (sin yaw, cos yaw). */
function facingCentre(x, z) {
    return Math.round(Math.atan2(-x, -z) * 100) / 100;
}
const spawns = [
    { x: -20, y: ROOF, z: -21, yaw: facingCentre(-20, -21) }, // south west roof
    { x: 20, y: ROOF, z: -21, yaw: facingCentre(20, -21) }, // south east roof
    { x: -20, y: ROOF, z: 20, yaw: facingCentre(-20, 20) }, // north west roof
    { x: 26, y: WALK, z: 10, yaw: facingCentre(26, 10) }, // east walkway
    { x: 6, y: WALK, z: -26, yaw: facingCentre(6, -26) }, // south walkway
    { x: -15, y: WALK, z: 26, yaw: facingCentre(-15, 26) }, // north walkway
];
const pads = [
    { id: "P9", kind: Kind.Warhead, x: 0, y: PLAT, z: 0, label: "Warhead" },
    { id: "P1", kind: Kind.Sticky, x: 0, y: 0, z: 0, label: "Sticky" },
    { id: "P2", kind: Kind.Impact, x: 0, y: WALK, z: -26, label: "Impact" },
    { id: "P3", kind: Kind.Impact, x: -9, y: WALK, z: 26, label: "Impact" },
    { id: "P4", kind: Kind.Bouncer, x: -14.5, y: 0, z: -15, label: "Bouncer" },
    { id: "P5", kind: Kind.None, x: 17, y: 0, z: -15.5, label: "Smoke" },
    { id: "P6", kind: Kind.None, x: 8.5, y: LANDING, z: -7.5, label: "Bubble" },
    { id: "P7", kind: Kind.None, x: 13, y: 0, z: 15.5, label: "Overshield" },
    { id: "P8", kind: Kind.None, x: -26.5, y: ROOF, z: 26.5, label: "Camo" },
];
const NODES = [
    // floor
    /* 0 */ [0.5, 0, -0.5], // under the platform, by the Sticky pad
    /* 1 */ [2, 0, -12], // south floor
    /* 2 */ [0, 0, 13], // north floor
    /* 3 */ [-14.5, 0, -15], // the Bouncer lane
    /* 4 */ [17, 0, -15.5], // south east drop down, Smoke
    /* 5 */ [13, 0, 15.5], // tower base, Overshield
    /* 6 */ [-15.5, 0, 17], // north ramp foot
    /* 7 */ [17, 0, -7], // east ramp feet, both the walkway ramp and the platform ramp
    /* 8 */ [-20, 0, 0], // west lane
    /* 9 */ [-12, 0, -18], // south walkway west ramp foot
    /* 10 */ [12, 0, -18], // south walkway east ramp foot
    /* 11 */ [-9, 0, 7], // north west floor
    /* 12 */ [12, 0, 4], // east lane north
    /* 13 */ [-13, 0, 12], // by the north west container
    /* 14 */ [-18, 0, 6], // west ramp foot
    /* 15 */ [-14.5, 0, -8], // below the hut step
    /* 16 */ [-7, 0, -12.5], // below the south crate stack
    /* 17 */ [21, 0, 12], // below the north east roof step
    // the ring
    /* 18 */ [-12, WALK, -26], // south walkway west
    /* 19 */ [0, WALK, -26], // south walkway mid, Impact
    /* 20 */ [12, WALK, -26], // south walkway east
    /* 21 */ [-20, ROOF, -20.5], // south west roof
    /* 22 */ [20, ROOF, -20.5], // south east roof
    /* 23 */ [-26, WALK, -10], // west walkway south
    /* 24 */ [-26, WALK, -2], // west walkway mid
    /* 25 */ [-26, WALK, 6], // west walkway, ramp top
    /* 26 */ [26, WALK, -6], // east walkway, ramp top
    /* 27 */ [26, WALK, 10], // east walkway north
    /* 28 */ [-21, ROOF, 20], // north west roof
    /* 29 */ [-26.5, ROOF, 26.5], // behind the north west tank, Camo
    /* 30 */ [-20, ROOF, 26], // north west roof, walkway end
    /* 31 */ [-15.5, WALK, 26], // north walkway, ramp top
    /* 32 */ [-9, WALK, 26], // north walkway mid, Impact
    /* 33 */ [-2, WALK, 26], // stair foot
    // the cage stair and the tower
    /* 34 */ [1.7, 3.5, 26], // step 5
    /* 35 */ [4.4, 5.0, 26], // step 10
    /* 36 */ [7.2, 6.5, 26], // step 15
    /* 37 */ [10.5, TOWER, 26], // tower top west
    /* 38 */ [15.5, TOWER, 22], // tower top east
    /* 39 */ [17, TOWER, 26], // tower top, ramp head
    /* 40 */ [21, 5.11, 26], // tower ramp upper
    /* 41 */ [24, 3.43, 26], // tower ramp lower
    /* 42 */ [27, ROOF, 26], // tower ramp foot on the north east roof
    /* 43 */ [26.5, ROOF, 21], // north east roof east
    /* 44 */ [19.5, ROOF, 21], // north east roof west
    /* 45 */ [21.25, 1.1, 16.75], // the step up to the north east roof
    // platform and its approaches
    /* 46 */ [0, PLAT, 0], // Warhead
    /* 47 */ [-4, PLAT, 0], // platform west, plank head
    /* 48 */ [4, PLAT, -1], // platform east, by the column
    /* 49 */ [-2, PLAT, -4], // platform south
    /* 50 */ [8.5, PLAT, -0.5], // column top
    /* 51 */ [8.5, LANDING, -7.5], // the landing, Bubble
    /* 52 */ [-13.5, HUT, 0], // hut top
    /* 53 */ [-14.75, 1.1, -4.25], // the hut step
    /* 54 */ [-8.25, 1.2, -8.2], // south stack, AC unit
    /* 55 */ [-4, 2.5, -8.2], // south stack, container
    /* 56 */ [16.75, 1.1, -21.25], // south east drop down step
    /* 57 */ [26.5, ROOF, -19], // south east roof, east walkway end
    /* 58 */ [-26, ROOF, 21], // north west roof, walkway end
    /* 59 */ [20, ROOF, -26], // south east roof, south walkway end
    /* 60 */ [-20, ROOF, -26], // south west roof, south walkway end
    /* 61 */ [-26.5, ROOF, -19], // south west roof, west walkway end
];
const EDGES = [
    // floor
    [0, 1], [0, 2], [0, 12], [0, 11], [0, 15], [0, 7],
    [1, 9], [1, 10], [1, 16],
    [2, 5], [2, 13], [2, 11], [2, 12],
    [3, 9], [3, 15], [3, 8],
    [4, 10], [4, 7], [4, 56],
    [5, 12], [5, 17],
    [6, 13], [6, 31],
    [7, 12], [7, 51], [7, 26],
    [8, 14], [8, 13], [8, 15],
    [9, 18], [10, 20],
    [11, 13],
    [12, 17],
    [14, 25],
    [15, 53],
    [16, 54],
    [17, 45],
    // the ring
    [18, 19], [19, 20], [18, 60], [60, 21], [20, 59], [59, 22],
    [21, 61], [61, 23], [23, 24], [24, 25], [25, 58], [58, 28], [58, 29],
    [22, 57], [57, 26], [26, 27], [27, 43],
    [28, 30], [29, 30], [30, 31], [31, 32], [32, 33],
    // stair and tower
    [33, 34], [34, 35], [35, 36], [36, 37], [37, 38], [37, 39], [38, 39],
    [39, 40], [40, 41], [41, 42], [42, 43], [43, 44], [44, 45],
    // platform and approaches
    [46, 47], [46, 48], [46, 49], [47, 49], [48, 49], [48, 50], [50, 51],
    [47, 52], [52, 53],
    [49, 55], [55, 54],
    [22, 56],
];
function buildWaypoints() {
    const nodes = NODES.map(([x, y, z], id) => ({ id, x, y, z, links: [] }));
    const link = (from, to) => {
        const dy = nodes[to].y - nodes[from].y;
        nodes[from].links.push(dy >= 0.4 ? { to, jump: true } : { to });
    };
    for (const [a, b] of EDGES) {
        link(a, b);
        link(b, a);
    }
    return nodes;
}
export const ROOFTOPS = {
    name: "Rooftops",
    half: ROOFTOPS_HALF,
    boxes,
    spawns,
    pads,
    waypoints: buildWaypoints(),
};
//# sourceMappingURL=rooftops.js.map